// MediaPicker — componente base reutilizable para subir 1 archivo (imagen o video).
//
// Maneja: selección desde galería / cámara / archivos del sistema, validación,
// extracción de metadata (duración, thumbnail, dimensiones), preview y replace.
//
// API:
//   <MediaPicker
//     accept="image" | "video" | "both"
//     value={ { dataUrl, type, durationSec, ... } | null }
//     onChange={(media | null) => ...}
//     compact={false}                    // versión inline para forms
//     maxSizeMB                          // override del default
//     maxDurationSec                     // override del default (solo video)
//     allowCamera={true}                 // mostrar botón cámara en mobile
//     placeholder="Sube una foto o video"
//   />
//
// Se llama onChange(null) cuando el usuario cancela.
// La forma de `media` que se devuelve:
//   { dataUrl, type:'image'|'video', mime, sizeMB, durationSec?, width?, height?, thumbnailDataUrl?, name }

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Image as ImageIcon, Video, Camera, Upload, X, RefreshCw, Loader2, AlertCircle, Crop } from 'lucide-react';
import {
  validateFile, readAsDataUrl, extractVideoMetadata, extractImageMetadata,
  compressImage, formatBytes, formatDuration, isMobileDevice,
  ACCEPTED_IMAGE_TYPES, ACCEPTED_VIDEO_TYPES, DEFAULT_LIMITS
} from '@/utils/mediaUtils';
import { getPolicyForContext } from '@/utils/mediaPolicy';
import { MediaCropper, ASPECT_RATIOS, VIDEO_QUALITIES } from './MediaCropper';
import { tr } from '@/contexts/LanguageContext';

export const MediaPicker = ({
  accept = 'both',
  value = null,
  onChange,
  compact = false,
  maxSizeMB,                          // override manual (si se pasa, ignora policy)
  maxDurationSec,                     // override manual (si se pasa, ignora policy)
  policyContext = null,               // resuelve límites desde el store admin
  allowCamera = true,
  enableCrop = true,                  // recortador automático para imágenes
  defaultAspect = '1:1',              // aspect inicial del cropper
  defaultQuality = 'fhd',             // 'fhd' | '4k'
  placeholder = tr("Selecciona una foto o video"),
  testIdPrefix = 'media-picker'
}) => {
  const galleryRef = useRef(null);
  const cameraPhotoRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [mobile] = useState(isMobileDevice());
  const [cropperSrc, setCropperSrc] = useState(null);  // dataUrl pendiente de recorte
  const [cropperBaseMedia, setCropperBaseMedia] = useState(null); // metadata mientras se recorta

  // ===== Resolución de límites efectivos =====
  // Prioridad: override manual > policy del admin > defaults
  const policy = policyContext ? getPolicyForContext(policyContext) : null;
  const effectiveImageMaxMB = maxSizeMB ?? policy?.imageMaxMB ?? DEFAULT_LIMITS.imageMaxMB;
  const effectiveVideoMaxMB = maxSizeMB ?? policy?.videoMaxMB ?? DEFAULT_LIMITS.videoMaxMB;
  const effectiveVideoMaxSec = maxDurationSec ?? policy?.videoMaxSec ?? DEFAULT_LIMITS.videoMaxSec;

  // ===== Calidad: parámetros del cropper y selector de video =====
  const imageQualityOptions = policy?.imageQualityOptions || ['fhd', '4k'];
  const imageQualityDefault = policy?.imageQualityDefault || defaultQuality;
  const imageQualityLocked  = policy?.imageQualityLocked || false;
  const videoQualityOptions = policy?.videoQualityOptions || ['fhd'];
  const videoQualityDefault = policy?.videoQualityDefault || 'fhd';
  const videoQualityLocked  = policy?.videoQualityLocked ?? (videoQualityOptions.length <= 1);
  const videoMinHeight      = policy?.videoMinHeight || null;

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    setProgress(10);
    try {
      const opts = {
        accept,
        imageMaxMB: effectiveImageMaxMB,
        videoMaxMB: effectiveVideoMaxMB
      };
      const v = validateFile(file, opts);
      if (!v.valid) { setError(v.error); setBusy(false); setProgress(0); return; }
      setProgress(35);

      let media;
      if (v.kind === 'image') {
        const [dataUrl, dims] = await Promise.all([
          compressImage(file, { maxDim: 1600, qualityJpeg: 0.85 }),
          extractImageMetadata(file)
        ]);
        setProgress(85);
        media = {
          type: 'image',
          mime: file.type || 'image/jpeg',
          dataUrl,
          name: file.name,
          sizeMB: v.sizeMB,
          width: dims.width,
          height: dims.height
        };
        // Abrir cropper si está habilitado (solo imágenes)
        if (enableCrop) {
          setCropperBaseMedia(media);
          setCropperSrc(dataUrl);
          setBusy(false);
          setProgress(0);
          return;
        }
      } else {
        // Video
        const [dataUrl, meta] = await Promise.all([
          readAsDataUrl(file),
          extractVideoMetadata(file).catch(() => ({ duration: null, width: 0, height: 0, thumbnailDataUrl: null }))
        ]);
        if (effectiveVideoMaxSec && meta.duration && meta.duration > effectiveVideoMaxSec) {
          setError(tr("El video excede {0} (dura {1})", formatDuration(effectiveVideoMaxSec), formatDuration(meta.duration)));
          setBusy(false); setProgress(0); return;
        }
        // Warning (no bloquea) si la altura nativa no llega al mínimo recomendado
        let qualityWarning = null;
        if (videoMinHeight && meta.height && meta.height < videoMinHeight) {
          qualityWarning = tr("Tu video es {0}×{1}. Recomendado mínimo {2}p para este contexto.", meta.width, meta.height, videoMinHeight);
        }
        setProgress(85);
        media = {
          type: 'video',
          mime: file.type || 'video/mp4',
          dataUrl,
          name: file.name,
          sizeMB: v.sizeMB,
          durationSec: meta.duration || null,
          width: meta.width,
          height: meta.height,
          thumbnailDataUrl: meta.thumbnailDataUrl || null,
          quality: videoQualityDefault,         // calidad sugerida — backend re-encoda a esto
          qualityWarning
        };
      }
      setProgress(100);
      onChange?.(media);
      setTimeout(() => { setBusy(false); setProgress(0); }, 250);
    } catch (e) {
      setError(e?.message || tr("Error procesando el archivo"));
      setBusy(false);
      setProgress(0);
    }
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) handleFile(file);
  };

  const handleClear = () => {
    onChange?.(null);
    setError(null);
  };

  // Apply el resultado del cropper al media actual y enviar onChange
  const handleCropApply = ({ dataUrl, aspect, quality, width, height }) => {
    const final = {
      ...(cropperBaseMedia || {}),
      dataUrl,
      width,
      height,
      cropAspect: aspect,
      quality,
      sizeMB: dataUrl.length / (1024 * 1024 * 1.37)  // estimación base64
    };
    onChange?.(final);
    setCropperSrc(null);
    setCropperBaseMedia(null);
  };

  // Si el usuario cancela el cropper, descarta el archivo (no quedó nada confirmado)
  const handleCropCancel = (open) => {
    if (!open) {
      setCropperSrc(null);
      setCropperBaseMedia(null);
    }
  };

  // Reabrir el cropper desde la preview (solo aplicable a imágenes)
  const handleReopenCropper = () => {
    if (!value?.dataUrl || value.type !== 'image') return;
    setCropperBaseMedia(value);
    setCropperSrc(value.dataUrl);
  };

  const acceptStr = accept === 'image' ? ACCEPTED_IMAGE_TYPES.join(',')
    : accept === 'video' ? ACCEPTED_VIDEO_TYPES.join(',')
    : [...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES].join(',');

  // ===== PREVIEW MODE =====
  if (value?.dataUrl) {
    return (
      <div data-testid={`${testIdPrefix}-preview`} className="space-y-2">
        <div className="relative bg-card border-2 border-primary/40 rounded-xl overflow-hidden">
          {value.type === 'image' ? (
            <img
              src={value.dataUrl}
              alt={value.name || 'Preview'}
              className="w-full max-h-[420px] object-contain bg-black"
              data-testid={`${testIdPrefix}-preview-image`}
            />
          ) : (
            <video
              src={value.dataUrl}
              controls
              playsInline
              className="w-full max-h-[420px] bg-black"
              data-testid={`${testIdPrefix}-preview-video`}
            />
          )}
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 w-9 h-9 rounded-full bg-background/80 backdrop-blur border border-border flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors"
            data-testid={`${testIdPrefix}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap text-[11px] text-muted-foreground">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono uppercase">{value.type === 'image' ? 'IMG' : 'VID'}</span>
            <span>{value.name || '—'}</span>
            <span>{formatBytes(value.sizeMB ? value.sizeMB * 1024 * 1024 : 0)}</span>
            {value.durationSec ? <span>{formatDuration(value.durationSec)}</span> : null}
            {value.width && value.height ? <span>{value.width}×{value.height}</span> : null}
            {value.cropAspect ? (
              <span className="bg-primary/15 border border-primary/40 text-primary rounded px-1.5 py-0.5 font-semibold" data-testid={`${testIdPrefix}-crop-badge`}>
                {value.cropAspect}{value.quality ? ` · ${(value.quality || '').toUpperCase()}` : ''}
              </span>
            ) : value.quality && value.type === 'video' ? (
              <span className="bg-primary/15 border border-primary/40 text-primary rounded px-1.5 py-0.5 font-semibold" data-testid={`${testIdPrefix}-quality-badge`}>
                {VIDEO_QUALITIES[value.quality]?.short || value.quality.toUpperCase()}
              </span>
            ) : null}
          </div>
          <div className="flex gap-1.5">
            {value.type === 'image' && enableCrop && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReopenCropper}
                className="text-xs gap-1"
                data-testid={`${testIdPrefix}-recrop`}
              >
                <Crop className="w-3 h-3" /> Recortar
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => galleryRef.current?.click()}
              className="text-xs gap-1"
              data-testid={`${testIdPrefix}-replace`}
            >
              <RefreshCw className="w-3 h-3" />{' '}{tr("Cambiar")}</Button>
          </div>
        </div>

        {/* Para videos: chooser de aspect ratio + calidad */}
        {value.type === 'video' && (
          <div className="bg-card border border-border rounded-lg p-2.5 space-y-2.5" data-testid={`${testIdPrefix}-video-aspect`}>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1.5">{tr("Aspect ratio del video (display)")}</p>
              <div className="grid grid-cols-3 gap-1.5">
                {ASPECT_RATIOS.map((ar) => {
                  const Icon = ar.icon;
                  const active = value.cropAspect === ar.id;
                  return (
                    <button
                      key={ar.id}
                      type="button"
                      onClick={() => onChange?.({ ...value, cropAspect: ar.id })}
                      data-testid={`${testIdPrefix}-video-aspect-${ar.id.replace(':', '-')}`}
                      className={`flex items-center justify-center gap-1 py-1.5 rounded border text-[11px] transition-colors ${
                        active
                          ? 'border-primary bg-primary/10 text-primary font-semibold'
                          : 'border-border bg-background text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      <Icon className="w-3 h-3" />{ar.id}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Calidad de video */}
            {!videoQualityLocked && videoQualityOptions.length > 1 && (
              <div data-testid={`${testIdPrefix}-video-quality`}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1.5">{tr("Calidad del video {0}", value.height ? `(nativo ${value.width}×${value.height})` : '')}</p>
                <div className={`grid gap-1.5 grid-cols-${videoQualityOptions.length}`}>
                  {videoQualityOptions.map((qid) => {
                    const q = VIDEO_QUALITIES[qid];
                    if (!q) return null;
                    const active = (value.quality || videoQualityDefault) === qid;
                    const downscaled = value.height && q.height > value.height;
                    return (
                      <button
                        key={qid}
                        type="button"
                        onClick={() => onChange?.({ ...value, quality: qid })}
                        data-testid={`${testIdPrefix}-video-quality-${qid}`}
                        className={`flex flex-col items-center justify-center py-1.5 px-1 rounded border text-[10px] transition-colors ${
                          active
                            ? 'border-primary bg-primary/10 text-primary font-semibold'
                            : 'border-border bg-background text-muted-foreground hover:border-primary/40'
                        }`}
                        title={downscaled ? tr("Tu video nativo no llega a esta resolución") : q.label}
                      >
                        <span className="font-bold">{q.short}</span>
                        <span className="text-[9px] opacity-80">{q.height}p</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {videoQualityLocked && (
              <div className="flex items-center gap-1.5 bg-primary/10 border border-primary/30 rounded p-1.5" data-testid={`${testIdPrefix}-video-quality-locked`}>
                <span className="text-[10px] text-foreground font-semibold uppercase tracking-widest">{tr("Calidad fija:")}</span>
                <span className="text-[11px] text-primary font-bold">{VIDEO_QUALITIES[value.quality || videoQualityDefault]?.label || 'FHD · 1080p'}</span>
              </div>
            )}

            {value.qualityWarning && (
              <div className="flex items-start gap-1.5 bg-amber-500/10 border border-amber-500/40 text-amber-500 rounded p-1.5 text-[11px]" data-testid={`${testIdPrefix}-video-quality-warning`}>
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{value.qualityWarning}</span>
              </div>
            )}

            <p className="text-[10px] text-muted-foreground italic">{tr("El re-encode real al aspect/calidad final lo hace el backend al subir. Aquí solo eliges el destino.")}</p>
          </div>
        )}

        <input ref={galleryRef} type="file" accept={acceptStr} onChange={onInputChange} className="hidden" data-testid={`${testIdPrefix}-input-gallery`} />

        {/* Cropper modal — disponible también desde preview ("Recortar") */}
        <MediaCropper
          open={!!cropperSrc}
          onOpenChange={handleCropCancel}
          src={cropperSrc}
          defaultAspect={value?.cropAspect || defaultAspect}
          defaultQuality={value?.quality || imageQualityDefault}
          qualityOptions={imageQualityOptions}
          lockQuality={imageQualityLocked}
          onApply={handleCropApply}
        />
      </div>
    );
  }

  // ===== EMPTY STATE — picker =====
  return (
    <div className={`relative ${compact ? '' : 'p-4'}`} data-testid={`${testIdPrefix}-empty`}>
      <div className={`bg-card border-2 border-dashed border-border hover:border-primary/60 transition-colors rounded-xl ${compact ? 'p-4' : 'p-6'} text-center`}>
        {busy ? (
          <div className="space-y-3" data-testid={`${testIdPrefix}-busy`}>
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
            <p className="text-sm text-foreground font-semibold">{tr("Procesando archivo…")}</p>
            <div className="h-1.5 bg-secondary rounded-full overflow-hidden max-w-xs mx-auto">
              <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-[10px] text-muted-foreground tabular-nums">{progress}%</p>
          </div>
        ) : (
          <>
            <Upload className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} text-primary mx-auto mb-3`} />
            <p className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-foreground mb-1`}>{placeholder}</p>
            <p className="text-xs text-muted-foreground mb-4" data-testid={`${testIdPrefix}-limits-info`}>
              {accept === 'image' && (
                <>{tr("JPG, PNG, WEBP{0}máx {1}", ' · ', effectiveImageMaxMB ? `${effectiveImageMaxMB} MB` : 'ilimitado')}</>
              )}
              {accept === 'video' && (
                <>MP4, MOV, WEBM{tr("· duración")}{effectiveVideoMaxSec ? tr("máx {0}", formatDuration(effectiveVideoMaxSec)) : tr("sin límite")}{' · peso '}{effectiveVideoMaxMB ? tr("máx {0} MB", effectiveVideoMaxMB) : tr("sin límite")}</>
              )}
              {accept === 'both' && (
                <>
                  IMG {effectiveImageMaxMB ? `≤${effectiveImageMaxMB} MB` : tr("sin límite")}
                  {' · VID '}
                  {effectiveVideoMaxSec ? `≤${formatDuration(effectiveVideoMaxSec)}` : tr("sin límite")}
                  {', '}
                  {effectiveVideoMaxMB ? `≤${effectiveVideoMaxMB} MB` : tr("sin límite")}
                </>
              )}
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button
                onClick={() => galleryRef.current?.click()}
                className="bg-primary text-primary-foreground gap-2"
                data-testid={`${testIdPrefix}-btn-gallery`}
                size={compact ? 'sm' : 'default'}
              >
                <ImageIcon className="w-4 h-4" />{' '}{tr("Galería / Archivos")}</Button>
              {allowCamera && mobile && accept !== 'video' && (
                <Button
                  variant="outline"
                  onClick={() => cameraPhotoRef.current?.click()}
                  className="gap-2"
                  data-testid={`${testIdPrefix}-btn-camera-photo`}
                  size={compact ? 'sm' : 'default'}
                >
                  <Camera className="w-4 h-4" />{' '}{tr("Cámara")}</Button>
              )}
              {allowCamera && mobile && accept !== 'image' && (
                <Button
                  variant="outline"
                  onClick={() => cameraVideoRef.current?.click()}
                  className="gap-2"
                  data-testid={`${testIdPrefix}-btn-camera-video`}
                  size={compact ? 'sm' : 'default'}
                >
                  <Video className="w-4 h-4" />{' '}{tr("Grabar")}</Button>
              )}
            </div>
          </>
        )}
      </div>

      {error && (
        <div
          className="mt-2 bg-destructive/10 border border-destructive/40 text-destructive text-xs rounded-lg p-2 flex items-start gap-2"
          data-testid={`${testIdPrefix}-error`}
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Inputs ocultos */}
      <input ref={galleryRef} type="file" accept={acceptStr} onChange={onInputChange} className="hidden" data-testid={`${testIdPrefix}-input-gallery`} />
      <input ref={cameraPhotoRef} type="file" accept="image/*" capture="environment" onChange={onInputChange} className="hidden" data-testid={`${testIdPrefix}-input-camera-photo`} />
      <input ref={cameraVideoRef} type="file" accept="video/*" capture="environment" onChange={onInputChange} className="hidden" data-testid={`${testIdPrefix}-input-camera-video`} />

      {/* Cropper modal — se abre automáticamente tras seleccionar una imagen */}
      <MediaCropper
        open={!!cropperSrc}
        onOpenChange={handleCropCancel}
        src={cropperSrc}
        defaultAspect={defaultAspect}
        defaultQuality={imageQualityDefault}
        qualityOptions={imageQualityOptions}
        lockQuality={imageQualityLocked}
        onApply={handleCropApply}
      />
    </div>
  );
};
